/**
 * AgreementHeader Component
 *
 * UI/Aesthetics: Header section for rebate agreement detail page with navigation, title, and key info
 * Functions:
 * - Back navigation with arrow button
 * - Agreement title and key details display
 * - Action buttons for edit and document viewing
 * - Responsive grid layout for agreement details
 * - Loading and error states
 * Previous: Used MUI Grid + IconButton + Tooltip + MUI icons + MD components with sx styling
 * Now: Uses Tailwind CSS Grid + shadcn Button + Tooltip + Lucide icons with native elements
 */

import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  PencilSimpleIcon,
  TrashIcon,
  FloppyDiskIcon,
  XIcon,
  CircleNotchIcon,
  ArrowsClockwiseIcon,
  UploadIcon,
  FileTextIcon,
  FileIcon,
  ArrowSquareOutIcon,
  CopyIcon,
} from '@phosphor-icons/react';
import { Link } from '@tanstack/react-router';
import {
  useResetForecast,
  useResetForecastAndReported,
} from 'src/api/rebates/useResetForecast';
import { RebateAgreementWithStructure } from 'src/api/rebates/types';
import { Badge } from '@/components/ui/badge';
import SupplierPill from 'src/components/company/SupplierPill';
import {
  SingleSelect,
  SingleSelectOption,
} from '@/components/common/SingleSelect';
import ConfirmDialog from 'src/components/common/ConfirmDialog';
import useUpdateRebateAgreement from 'src/api/rebates/useUpdateRebateAgreementStatus';
import { DatePicker } from 'src/components/common/date-pickers/date-picker';
import DocumentIcons from 'src/components/rebates/DocumentIcons';
import { StatusSummaryCard } from './StatusSummaryCard';
import { AuthContext } from '@/context/auth.context';
import { useViewMode } from '@/context/view-mode.context';
import { isAdmin } from '@/utils/user';
import { useRebateAgreementTags } from 'src/api/core/enumOptionAssociations/useEnumOptionAssociations';
import { TagsDisplay } from 'src/components/rebates/TagsDisplay';
import { SupplierAutocomplete } from '@/components/company/SupplierAutocomplete';
import { REBATES_SYSTEM_ENUMS } from '@erp/shared-types';
import {
  useUploadAgreementDocument,
  useRemoveAgreementDocument,
} from 'src/api/rpc/hooks/rebates/useAgreementDocuments';
// Simple date formatting helper
const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
};

// Helper to create a Date object from YYYY-MM-DD string without timezone issues
const createLocalDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day); // month is 0-indexed
};

// Helper to convert Date to YYYY-MM-DD string
const formatDateToString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Calculate which calendar years an agreement spans
const getAvailableYears = (startDate: string, endDate: string): number[] => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const years: number[] = [];

  for (
    let year = start.getUTCFullYear();
    year <= end.getUTCFullYear();
    year++
  ) {
    years.push(year);
  }
  return years;
};

interface AgreementHeaderProps {
  agreement: RebateAgreementWithStructure;
  isLoading: boolean;
  onBack: () => void;
  onViewDocument?: (documentId: string) => void;
  onDelete?: () => void;
  onUpdate?: () => void; // Called after successful update to refresh data
  onSupplierChange?: (newSupplierId: string) => void; // Called when supplier changes in draft mode
  onForecastReset?: () => void; // Called after forecast reset to refresh data
  rebateRulesCount: number; // Number of rebate rules for validation
  validationErrors: string[]; // Validation errors that prevent finalization
  lastDataIngestionDate?: string | null; // Max date of all invoice line item ranges
  // Multi-year agreement support
  selectedYear?: number | null; // Currently selected year for multi-year agreements
  onYearChange?: (year: number) => void; // Callback when year selection changes
}

const AgreementHeader: React.FC<AgreementHeaderProps> = ({
  agreement,
  isLoading,
  onBack,
  onViewDocument,
  onDelete,
  onUpdate,
  onSupplierChange,
  onForecastReset,
  rebateRulesCount: _rebateRulesCount,
  validationErrors,
  lastDataIngestionDate,
  selectedYear,
  onYearChange,
}) => {
  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editStatus, setEditStatus] = useState<'draft' | 'finalized'>('draft');
  const [editStartDate, setEditStartDate] = useState<Date>();
  const [editEndDate, setEditEndDate] = useState<Date>();
  const [editNotes, setEditNotes] = useState<string>('');
  const [editRebatePaymentTerms, setEditRebatePaymentTerms] =
    useState<string>('');
  const [editPurchasePaymentTerms, setEditPurchasePaymentTerms] =
    useState<string>('');
  const [editIsHistorical, setEditIsHistorical] = useState<boolean>(false);
  const [editSupplierId, setEditSupplierId] = useState<string | undefined>();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmDialogConfig, setConfirmDialogConfig] = useState<{
    title: string;
    message: string;
    confirmText: string;
    dangerous?: boolean;
    isBlocking?: boolean; // True if this is a blocking dialog that doesn't proceed with update
  }>({
    title: '',
    message: '',
    confirmText: '',
    isBlocking: false,
  });

  const updateMutation = useUpdateRebateAgreement(agreement?.id || '');

  // Forecast reset state and mutations (admin only)
  const [resetForecastDialogOpen, setResetForecastDialogOpen] = useState(false);
  const [resetWithReportedDialogOpen, setResetWithReportedDialogOpen] =
    useState(false);
  const resetForecastMutation = useResetForecast();
  const resetWithReportedMutation = useResetForecastAndReported();

  // Document management state and mutations
  const [documentToRemove, setDocumentToRemove] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [showRemoveDocumentDialog, setShowRemoveDocumentDialog] =
    useState(false);
  const uploadDocumentMutation = useUploadAgreementDocument();
  const removeDocumentMutation = useRemoveAgreementDocument();
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Get current user and view mode for admin check
  const { currentUser } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const userIsAdmin = isAdmin(currentUser, viewMode);

  // Get tags for this agreement
  const {
    currentTags,
    availableTags,
    isLoading: tagsLoading,
    isUpdating: tagsUpdating,
    handleAddTag,
    handleRemoveTag,
    currentTagIds,
  } = useRebateAgreementTags(agreement?.id || '');

  // Find the "Duplicate" tag ID to lock it from add/remove
  const lockedTagIds = useMemo(() => {
    const duplicateTag = availableTags.find(
      (tag) =>
        tag.name === REBATES_SYSTEM_ENUMS.AGREEMENT_TAGS.options.DUPLICATE.name,
    );
    return duplicateTag ? new Set([duplicateTag.id]) : new Set<string>();
  }, [availableTags]);

  // Multi-year agreement support
  const availableYears = useMemo(() => {
    if (!agreement) return [];
    return getAvailableYears(agreement.start_date, agreement.end_date);
  }, [agreement]);

  const isMultiYear = availableYears.length > 1;

  const yearOptions = useMemo((): SingleSelectOption[] => {
    return availableYears.map((year) => ({
      id: String(year),
      label: String(year),
      value: year,
    }));
  }, [availableYears]);

  const selectedYearOption = useMemo((): SingleSelectOption | null => {
    if (!selectedYear) return null;
    return yearOptions.find((opt) => opt.value === selectedYear) || null;
  }, [selectedYear, yearOptions]);

  const handleYearChange = (option: SingleSelectOption | null) => {
    if (option && onYearChange) {
      onYearChange(option.value as number);
    }
  };

  // Initialize edit values when entering edit mode
  useEffect(() => {
    if (isEditing && agreement) {
      setEditName(agreement.name);
      setEditStatus(agreement.status);
      setEditStartDate(createLocalDate(agreement.start_date));
      setEditEndDate(createLocalDate(agreement.end_date));
      setEditNotes(agreement.notes || '');
      setEditRebatePaymentTerms(agreement.rebate_payment_terms || '');
      setEditPurchasePaymentTerms(agreement.purchase_payment_terms || '');
      setEditIsHistorical(agreement.is_historical || false);
      setEditSupplierId(agreement.supplier?.id);
    }
  }, [isEditing, agreement]);

  // Check if dates are valid (end date must be after start date)
  const isValidDateRange =
    !editStartDate || !editEndDate || editEndDate > editStartDate;

  const hasChanges =
    agreement &&
    isValidDateRange &&
    (editName !== agreement.name ||
      editStatus !== agreement.status ||
      editNotes !== (agreement.notes || '') ||
      editRebatePaymentTerms !== (agreement.rebate_payment_terms || '') ||
      editPurchasePaymentTerms !== (agreement.purchase_payment_terms || '') ||
      editIsHistorical !== (agreement.is_historical || false) ||
      editSupplierId !== agreement.supplier?.id ||
      (editStartDate ? formatDateToString(editStartDate) : '') !==
        agreement.start_date ||
      (editEndDate ? formatDateToString(editEndDate) : '') !==
        agreement.end_date);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (agreement) {
      setEditName(agreement.name);
      setEditStatus(agreement.status);
      setEditStartDate(createLocalDate(agreement.start_date));
      setEditEndDate(createLocalDate(agreement.end_date));
      setEditNotes(agreement.notes || '');
      setEditRebatePaymentTerms(agreement.rebate_payment_terms || '');
      setEditPurchasePaymentTerms(agreement.purchase_payment_terms || '');
      setEditIsHistorical(agreement.is_historical || false);
      setEditSupplierId(agreement.supplier?.id);
    }
    setIsEditing(false);
  };

  const handleStatusToggle = (checked: boolean) => {
    setEditStatus(checked ? 'finalized' : 'draft');
  };

  const handleSave = () => {
    if (!hasChanges) {
      setIsEditing(false);
      return;
    }

    // Check if supplier is changing (only allowed for draft agreements)
    if (
      agreement &&
      editSupplierId &&
      editSupplierId !== agreement.supplier?.id &&
      agreement.status === 'draft'
    ) {
      // Close edit mode and call the supplier change handler
      setIsEditing(false);
      if (onSupplierChange) {
        onSupplierChange(editSupplierId);
      }
      return;
    }

    // Check if status is changing and show appropriate confirmation
    if (agreement && editStatus !== agreement.status) {
      if (agreement.status === 'finalized' && editStatus === 'draft') {
        // Changing from finalized to draft - check if CSV files have been processed
        if (agreement.has_processed_invoices && !userIsAdmin) {
          // Block the transition if invoices have been processed (non-admin users)
          setConfirmDialogConfig({
            title: 'Cannot Change to Draft',
            message: `This agreement has processed CSV data and cannot be reverted to draft status.\n\nTo modify rules or product groups, you must create a new agreement instead.\n\nOnce CSV files have been processed for a finalized agreement, the agreement becomes locked to ensure data integrity.`,
            confirmText: 'Understood',
            dangerous: false,
            isBlocking: true, // This is a blocking dialog
          });
          setShowConfirmDialog(true);
          return; // Don't proceed with the update
        }

        // Admin override or no invoices processed - warn about invoice dissociation
        const adminOverrideWarning =
          agreement.has_processed_invoices && userIsAdmin
            ? '\n\n⚠️ ADMIN OVERRIDE: You are forcing this status change despite processed CSV data. This will delete all associated invoices and line items.'
            : '';

        setConfirmDialogConfig({
          title: 'Change Status to Draft',
          message: `Are you sure you want to change this agreement from Finalized to Draft?\n\nThis will:\n• Allow editing of rebate rules again\n• Dissociate all processed invoices from this agreement\n• Remove all invoice associations and line items\n\nThis action cannot be undone. Invoices will need to be re-processed if you finalize again.${adminOverrideWarning}`,
          confirmText: 'Change to Draft',
          dangerous: true,
        });
      } else if (agreement.status === 'draft' && editStatus === 'finalized') {
        // Changing from draft to finalized
        setConfirmDialogConfig({
          title: 'Finalize Agreement',
          message: `Are you sure you want to finalize this agreement?\n\nThis will:\n• Lock all rebate rules from further editing\n• Enable CSV transformation and data processing\n• This action can be reversed but will require re-processing data`,
          confirmText: 'Finalize Agreement',
          dangerous: false,
        });
      }
      setShowConfirmDialog(true);
    } else {
      // No status change, just update directly
      performUpdate();
    }
  };

  const performUpdate = async () => {
    if (!agreement) return;

    try {
      const updateData: {
        name?: string;
        status?: 'draft' | 'finalized';
        startDate?: string;
        endDate?: string;
        notes?: string | null;
        rebatePaymentTerms?: string | null;
        purchasePaymentTerms?: string | null;
        isHistorical?: boolean;
      } = {};

      if (editName !== agreement.name) {
        updateData.name = editName;
      }

      if (editStatus !== agreement.status) {
        updateData.status = editStatus;
      }

      if (
        editStartDate &&
        formatDateToString(editStartDate) !== agreement.start_date
      ) {
        updateData.startDate = editStartDate.toISOString();
      }

      if (
        editEndDate &&
        formatDateToString(editEndDate) !== agreement.end_date
      ) {
        updateData.endDate = editEndDate.toISOString();
      }

      // Always include notes if they've changed from the original
      const originalNotes = agreement.notes || '';
      const currentNotes = editNotes.trim();

      if (currentNotes !== originalNotes) {
        updateData.notes = currentNotes || null;
      }

      // Always include rebate payment terms if they've changed from the original
      const originalRebateTerms = agreement.rebate_payment_terms || '';
      const currentRebateTerms = editRebatePaymentTerms.trim();

      if (currentRebateTerms !== originalRebateTerms) {
        updateData.rebatePaymentTerms = currentRebateTerms || null;
      }

      // Always include purchase payment terms if they've changed from the original
      const originalPurchaseTerms = agreement.purchase_payment_terms || '';
      const currentPurchaseTerms = editPurchasePaymentTerms.trim();

      if (currentPurchaseTerms !== originalPurchaseTerms) {
        updateData.purchasePaymentTerms = currentPurchaseTerms || null;
      }

      if (editIsHistorical !== (agreement.is_historical || false)) {
        updateData.isHistorical = editIsHistorical;
      }

      await updateMutation.mutateAsync(updateData);

      setIsEditing(false);
      setShowConfirmDialog(false);

      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error updating agreement:', error);
    }
  };

  // Handle forecast reset (admin only)
  const handleResetForecast = async () => {
    if (!agreement?.id) return;
    try {
      await resetForecastMutation.mutateAsync({
        agreementIds: [agreement.id],
      });
      setResetForecastDialogOpen(false);
      if (onForecastReset) {
        onForecastReset();
      }
    } catch (error) {
      console.error('Error resetting forecast:', error);
    }
  };

  // Handle forecast reset with reported wipe (admin only)
  const handleResetForecastAndReported = async () => {
    if (!agreement?.id) return;
    try {
      await resetWithReportedMutation.mutateAsync({
        agreementIds: [agreement.id],
      });
      setResetWithReportedDialogOpen(false);
      if (onForecastReset) {
        onForecastReset();
      }
    } catch (error) {
      console.error('Error resetting forecast and reported:', error);
    }
  };

  // Handle document file selection
  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file || !agreement?.id) return;

    try {
      await uploadDocumentMutation.mutateAsync({
        agreementId: agreement.id,
        file,
      });
      // Refresh agreement data to show new document
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error uploading document:', error);
    }

    // Reset the file input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle document removal confirmation
  const handleRemoveDocumentClick = (doc: { id: string; name: string }) => {
    setDocumentToRemove(doc);
    setShowRemoveDocumentDialog(true);
  };

  const handleConfirmRemoveDocument = async () => {
    if (!documentToRemove || !agreement?.id) return;

    try {
      await removeDocumentMutation.mutateAsync({
        documentId: documentToRemove.id,
        agreementId: agreement.id,
      });
      setShowRemoveDocumentDialog(false);
      setDocumentToRemove(null);
      // Refresh agreement data
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error removing document:', error);
    }
  };

  // Helper to get file icon based on extension
  const getFileIcon = (fileName: string) => {
    const extension = fileName.toLowerCase().split('.').pop();
    switch (extension) {
      case 'pdf':
      case 'doc':
      case 'docx':
        return <FileTextIcon className="w-4 h-4 text-blue-600" />;
      case 'xls':
      case 'xlsx':
        return <FileTextIcon className="w-4 h-4 text-green-600" />;
      default:
        return <FileIcon className="w-4 h-4 text-gray-600" />;
    }
  };

  if (isLoading) {
    return (
      <div className="mb-6">
        <div className="flex items-center mb-4">
          <h1 className="text-2xl font-medium text-muted-foreground">
            Loading agreement...
          </h1>
        </div>
      </div>
    );
  }

  if (!agreement) {
    return (
      <div className="mb-6">
        <div className="flex items-center mb-4">
          <h1 className="text-2xl font-medium text-destructive">
            Agreement not found
          </h1>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6">
      {/* Title */}
      <div className="mb-4">
        <div className="flex items-center gap-3">
          {/* View Mode - Title */}
          {!isEditing && (
            <>
              <h1 className="text-2xl font-medium text-foreground">
                {agreement.name}
              </h1>
              {/* Year Selector for Multi-Year Agreements */}
              {isMultiYear && (
                <SingleSelect
                  options={yearOptions}
                  value={selectedYearOption}
                  onChange={handleYearChange}
                  placeholder="Year"
                  size="sm"
                  shouldAutoSort={false}
                  allowClear={false}
                />
              )}
            </>
          )}
          {/* View Mode Action Buttons - Right after name/year selector */}
          {!isEditing && (
            <div className="flex items-center gap-2 ml-4">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleEdit}
                      className="p-2 text-primary hover:text-primary/80"
                    >
                      <PencilSimpleIcon className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Edit Agreement</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {onDelete && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={onDelete}
                        className="p-2 text-destructive hover:text-destructive/80"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Delete Agreement</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {/* Admin-only forecast reset buttons */}
              {userIsAdmin && (
                <>
                  <div className="w-px h-6 bg-border mx-1" />
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setResetForecastDialogOpen(true)}
                          className="p-2 text-muted-foreground hover:text-foreground"
                        >
                          <ArrowsClockwiseIcon className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Reset Forecast (Admin)</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setResetWithReportedDialogOpen(true)}
                          className="p-2 text-orange-600 hover:text-orange-700"
                        >
                          <ArrowsClockwiseIcon className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Reset Forecast + Reported (Admin)</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </>
              )}
            </div>
          )}
        </div>
        {/* View Mode - Duplicated From Link */}
        {!isEditing && agreement.duplicated_from_agreement && (
          <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
            <CopyIcon className="h-4 w-4" />
            <span>Duplicated from:</span>
            <Link
              to="/rebates/agreements/$id"
              params={{ id: agreement.duplicated_from_agreement.id }}
              className="text-primary hover:underline inline-flex items-center gap-1"
            >
              {agreement.duplicated_from_agreement.name}
              <ArrowSquareOutIcon className="h-3 w-3" />
            </Link>
          </div>
        )}
        {/* View Mode - Status Summary */}
        {!isEditing && (
          <div className="mt-2">
            <StatusSummaryCard
              status={agreement.status}
              startDate={agreement.start_date}
              endDate={agreement.end_date}
              isHistorical={agreement.is_historical}
              isClosedOut={agreement.isClosedOut}
              closureMetadata={agreement.closureMetadata}
              lastDataIngestionDate={lastDataIngestionDate}
            />
          </div>
        )}
      </div>

      {/* Agreement Details */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* Left Column - Editable fields */}
        <div
          className={`md:col-span-6 space-y-6 relative ${
            isEditing
              ? 'bg-muted/30 border border-dashed border-primary/30 rounded-lg p-4 -m-4'
              : ''
          }`}
        >
          {isEditing && (
            <>
              {/* Cancel and Save Buttons - Top Right of Edit Box */}
              <div className="absolute top-4 right-4 flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancel}
                  disabled={updateMutation.isLoading}
                  className="gap-2"
                >
                  <XIcon className="h-4 w-4" />
                  Cancel
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleSave}
                  disabled={!hasChanges || updateMutation.isLoading}
                  className="gap-2"
                >
                  <FloppyDiskIcon className="h-4 w-4" />
                  {updateMutation.isLoading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>

              <p className="text-xs text-muted-foreground italic -mt-2 mb-2">
                Editing - changes require Save
              </p>
              {/* Agreement Name */}
              <div>
                <Label htmlFor="edit-name" className="text-sm font-medium">
                  Agreement Name
                </Label>
                <Input
                  id="edit-name"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="mt-2 text-xl font-medium"
                  placeholder="Enter agreement name"
                />
              </div>
            </>
          )}

          {/* Status - Only shown in edit mode */}
          {isEditing && (
            <div>
              <Label className="text-sm font-medium">Status</Label>
              <div className="mt-2 flex flex-col gap-2">
                <div className="flex items-center gap-3 h-10">
                  <Badge
                    variant="secondary"
                    className={
                      editStatus === 'draft'
                        ? 'bg-orange-100 text-orange-800'
                        : 'bg-gray-100 text-gray-400'
                    }
                  >
                    Draft
                  </Badge>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <Switch
                            checked={editStatus === 'finalized'}
                            onCheckedChange={handleStatusToggle}
                            disabled={
                              // Disable draft→finalized when validation errors exist
                              (validationErrors.length > 0 &&
                                editStatus === 'draft') ||
                              // Disable finalized→draft for non-admins with processed invoices
                              (editStatus === 'finalized' &&
                                agreement?.has_processed_invoices &&
                                !userIsAdmin)
                            }
                          />
                        </div>
                      </TooltipTrigger>
                      {editStatus === 'finalized' &&
                        agreement?.has_processed_invoices &&
                        !userIsAdmin && (
                          <TooltipContent>
                            <p>
                              Cannot revert to draft - CSV data has been
                              processed
                            </p>
                          </TooltipContent>
                        )}
                    </Tooltip>
                  </TooltipProvider>
                  <Badge
                    variant="default"
                    className={
                      editStatus === 'finalized'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-400'
                    }
                  >
                    Finalized
                  </Badge>
                </div>
                {validationErrors.length > 0 && editStatus === 'draft' && (
                  <div className="text-sm text-red-600 space-y-1">
                    <p className="font-medium">Cannot finalize agreement:</p>
                    {validationErrors.map((error, index) => (
                      <p key={index}>• {error}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Dates */}
          {isEditing ? (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label
                  className={`text-sm font-medium ${
                    !isValidDateRange && editStartDate && editEndDate
                      ? 'text-red-600'
                      : ''
                  }`}
                >
                  Start Date
                </Label>
                <DatePicker
                  value={editStartDate}
                  onSelect={setEditStartDate}
                  placeholder="Select start date"
                  className={`mt-2 w-full ${
                    !isValidDateRange && editStartDate && editEndDate
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                      : ''
                  }`}
                />
              </div>
              <div>
                <Label
                  className={`text-sm font-medium ${
                    !isValidDateRange && editStartDate && editEndDate
                      ? 'text-red-600'
                      : ''
                  }`}
                >
                  End Date
                </Label>
                <DatePicker
                  value={editEndDate}
                  onSelect={setEditEndDate}
                  placeholder="Select end date"
                  className={`mt-2 w-full ${
                    !isValidDateRange && editStartDate && editEndDate
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                      : ''
                  }`}
                />
              </div>
              {!isValidDateRange && editStartDate && editEndDate && (
                <div className="col-span-2 text-sm text-red-600">
                  End date must be after start date
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Start Date
                </label>
                <p className="text-base font-medium text-foreground mt-1">
                  {formatDate(agreement.start_date)}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  End Date
                </label>
                <p className="text-base font-medium text-foreground mt-1">
                  {formatDate(agreement.end_date)}
                </p>
              </div>
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-muted-foreground">
              Supplier
            </label>
            <div className="mt-2">
              {isEditing && agreement.status === 'draft' ? (
                <SupplierAutocomplete
                  supplierId={editSupplierId}
                  onSelect={(newSupplierId) => setEditSupplierId(newSupplierId)}
                  className="w-full"
                />
              ) : (
                <SupplierPill supplier={agreement.supplier} />
              )}
            </div>
          </div>

          {/* Notes Section */}
          <div>
            <label className="text-sm font-medium text-muted-foreground mb-2 block">
              Notes
            </label>
            {isEditing ? (
              <Textarea
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="Add notes about this agreement..."
                className="min-h-[100px] resize-y"
                rows={4}
              />
            ) : agreement.notes ? (
              <p className="text-sm text-foreground bg-muted/50 p-3 rounded-md whitespace-pre-wrap">
                {agreement.notes}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground italic">
                No notes available
              </p>
            )}
          </div>

          {/* Rebate Payment Terms Section */}
          <div>
            <label className="text-sm font-medium text-muted-foreground mb-2 block">
              Rebate Payment Terms
              <span className="text-xs font-normal text-muted-foreground ml-2">
                (How supplier pays rebates)
              </span>
            </label>
            {isEditing ? (
              <Input
                value={editRebatePaymentTerms}
                onChange={(e) => setEditRebatePaymentTerms(e.target.value)}
                placeholder="e.g., Quarterly, Credit memos within 45 days"
                className="w-full"
              />
            ) : agreement.rebate_payment_terms ? (
              <p className="text-sm text-foreground bg-muted/50 p-3 rounded-md">
                {agreement.rebate_payment_terms}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground italic">
                Not specified
              </p>
            )}
          </div>

          {/* Purchase Payment Terms Section */}
          <div>
            <label className="text-sm font-medium text-muted-foreground mb-2 block">
              Purchase Payment Terms
              <span className="text-xs font-normal text-muted-foreground ml-2">
                (How buyer pays invoices)
              </span>
            </label>
            {isEditing ? (
              <Input
                value={editPurchasePaymentTerms}
                onChange={(e) => setEditPurchasePaymentTerms(e.target.value)}
                placeholder="e.g., 2% 45, NET 46, Net 30"
                className="w-full"
              />
            ) : agreement.purchase_payment_terms ? (
              <p className="text-sm text-foreground bg-muted/50 p-3 rounded-md">
                {agreement.purchase_payment_terms}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground italic">
                Not specified
              </p>
            )}
          </div>

          {/* Historical Field - Only show in edit mode */}
          {isEditing && (
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-2 block">
                Historical
              </label>
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Mark this agreement as historical (excluded from new
                  processing)
                </p>
                <Switch
                  checked={editIsHistorical}
                  onCheckedChange={setEditIsHistorical}
                  disabled={updateMutation.isLoading}
                />
              </div>
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="md:col-span-6 space-y-6 pl-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                Created
              </label>
              <p className="text-base font-medium text-foreground mt-1">
                {formatDate(agreement.created_at)}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground">
                Last Updated
              </label>
              <p className="text-base font-medium text-foreground mt-1">
                {formatDate(agreement.updated_at)}
              </p>
            </div>
          </div>

          {/* Tags Section */}
          <div>
            <label className="text-sm font-medium text-muted-foreground mb-2 block">
              Tags
            </label>
            {tagsLoading ? (
              <div className="flex items-center gap-2">
                <CircleNotchIcon className="h-4 w-4 animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  Loading tags...
                </span>
              </div>
            ) : (
              <TagsDisplay
                currentTags={currentTags}
                availableTags={availableTags}
                currentTagIds={currentTagIds}
                onAddTag={handleAddTag}
                onRemoveTag={handleRemoveTag}
                isUpdating={tagsUpdating}
                layout="compact"
                isLoading={tagsLoading}
                lockedTagIds={lockedTagIds}
              />
            )}
          </div>

          {/* Documents Section - Always available for immediate add/remove */}
          <div>
            <label className="text-sm font-medium text-muted-foreground mb-2 block">
              Agreement Documents ({agreement.documents?.length || 0})
            </label>
            <div className="space-y-2">
              {/* Document list with remove buttons */}
              {agreement.documents && agreement.documents.length > 0 && (
                <div className="space-y-1">
                  {agreement.documents.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex items-center gap-2 p-2 bg-muted/50 rounded-md w-fit min-w-0 group"
                    >
                      <button
                        type="button"
                        onClick={() => onViewDocument?.(doc.id)}
                        className="flex items-center gap-2 min-w-0 hover:text-primary transition-colors"
                      >
                        {getFileIcon(doc.name)}
                        <span className="text-sm truncate max-w-[200px]">
                          {doc.name}
                        </span>
                      </button>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  handleRemoveDocumentClick({
                                    id: doc.id,
                                    name: doc.name,
                                  })
                                }
                                disabled={
                                  agreement.documents.length <= 1 ||
                                  removeDocumentMutation.isPending
                                }
                                className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 text-destructive hover:text-destructive/80 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                              >
                                <XIcon className="h-4 w-4" />
                              </Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {agreement.documents.length <= 1
                              ? 'Cannot remove the last document'
                              : 'Remove document'}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  ))}
                </div>
              )}

              {/* Add document button */}
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadDocumentMutation.isPending}
                  className="gap-2"
                >
                  {uploadDocumentMutation.isPending ? (
                    <>
                      <CircleNotchIcon className="h-4 w-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <UploadIcon className="h-4 w-4" />
                      Add Document
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog for Status Changes */}
      <ConfirmDialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={
          confirmDialogConfig.isBlocking
            ? () => setShowConfirmDialog(false) // Just close for blocking dialogs
            : performUpdate // Proceed with update for normal confirmations
        }
        title={confirmDialogConfig.title}
        message={confirmDialogConfig.message}
        confirmText={confirmDialogConfig.confirmText}
        loading={updateMutation.isLoading}
        dangerous={confirmDialogConfig.dangerous}
      />

      {/* Admin-only: Reset Forecast Dialog */}
      <ConfirmDialog
        open={resetForecastDialogOpen}
        onClose={() => setResetForecastDialogOpen(false)}
        onConfirm={handleResetForecast}
        title="Reset Forecast"
        message={`This will mark the forecast as stale and trigger a full regeneration.\n\nAll estimates, predictions, and true-up calculations will be recalculated from scratch.\n\nReported values will be preserved (they won't be overwritten if they already exist).`}
        confirmText="Reset Forecast"
        loading={resetForecastMutation.isPending}
        dangerous={false}
      />

      {/* Admin-only: Reset Forecast and Reported Dialog */}
      <ConfirmDialog
        open={resetWithReportedDialogOpen}
        onClose={() => setResetWithReportedDialogOpen(false)}
        onConfirm={handleResetForecastAndReported}
        title="Reset Forecast and Reported"
        message={`WARNING: This will permanently delete all REPORTED breakdown values for this agreement.\n\nThis action:\n1. Deletes all frozen "reported" amounts\n2. Marks the forecast as stale\n3. Triggers a full regeneration\n\nUse this when reported values are incorrect and need to be recalculated from scratch.`}
        confirmText="Reset Forecast and Reported"
        loading={resetWithReportedMutation.isPending}
        dangerous={true}
      />

      {/* Remove Document Confirmation Dialog */}
      <ConfirmDialog
        open={showRemoveDocumentDialog}
        onClose={() => {
          setShowRemoveDocumentDialog(false);
          setDocumentToRemove(null);
        }}
        onConfirm={handleConfirmRemoveDocument}
        title="Remove Document"
        message={`Are you sure you want to remove "${documentToRemove?.name}" from this agreement?`}
        confirmText="Remove"
        loading={removeDocumentMutation.isPending}
        dangerous={true}
      />
    </div>
  );
};

export default AgreementHeader;
